import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import { Layout, Sidebar, LayoutFooter } from '@thinknet-gmbh/platform-common-ui';

import Header from 'src/components/app/header';
import { getPath } from 'src/common/helpers/get-path.helpers';
import { usePermissionCheck } from 'src/hooks/permission-check.hook';
import { useCreateSupportRequest } from 'src/hooks/support-requests/create-support-request.hook';
import { useCheckIfAllowedSupportRequestCreation } from 'src/hooks/support-requests/allowed-support-request-creation.hook';
import { SIDEBAR_CONFIG } from './layout-wrapper.constants';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';

export const LayoutWrapper = ({
  children,
  isProtected,
}: {
  children: any;
  isProtected?: boolean;
}) => {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  const { checkIfHadPermission, checkIfHadPermissionAndRule } = usePermissionCheck();

  const [sidebarCollapsed, setCollapsed] = useState(false);
  const [mobileCollapsed, setMobileCollapsed] = useState(true);
  const navigate = useNavigate();
  const createSupportRequest = useCreateSupportRequest();
  const { allowedSupportRequestCreation } = useCheckIfAllowedSupportRequestCreation();
  const screen = useBreakpoint();

  const collapseFn = screen?.lg ? setCollapsed : setMobileCollapsed;

  const collapsed = screen?.lg ? sidebarCollapsed : mobileCollapsed;

  const handleCollapse = () => {
    collapseFn(prev => !prev);
  };

  const handleNavigate = (key: string) => {
    if (!screen?.lg) {
      setMobileCollapsed(prev => !prev);
    }
    navigate(key);
  };

  const menuItems = useMemo(
    () =>
      SIDEBAR_CONFIG.menuItems
        .filter(item => {
          if (item?.rule) {
            return checkIfHadPermissionAndRule(item?.permission, item.rule);
          }
          return !item.permission || checkIfHadPermission(item.permission);
        })
        .map(item => ({ ...item, title: t(`labels.${item.title}`) })),
    [t, checkIfHadPermission, checkIfHadPermissionAndRule]
  );

  return (
    <Layout>
      <Sidebar
        {...SIDEBAR_CONFIG}
        menuItems={menuItems}
        menuItemsPrimary={SIDEBAR_CONFIG.menuItemsPrimary.map(item => ({
          ...item,
          title: t(`labels.${item.title}`),
        }))}
        selectedKeys={[getPath(pathname)]}
        onItemClick={handleNavigate}
        collapsed={collapsed}
        sidebarToggle={handleCollapse}
      />
      <Layout>
        <Header sidebarCollapsed={collapsed} sidebarToggle={collapseFn} />
        {children}
        {isProtected && (
          <LayoutFooter
            t={t}
            isSupportRequestExist={!allowedSupportRequestCreation}
            onSubmitRequest={({ onClose, data }) => createSupportRequest(data).then(onClose)}
          />
        )}
      </Layout>
    </Layout>
  );
};

export default LayoutWrapper;
